import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, Directive, inject, input, model } from '@angular/core';
import { ClassValue } from 'clsx';
import { bui, UniqueId } from '../utils';

@Component({
  selector: 'collapsible',
  exportAs: 'collapsible',
  host: {
    '[class]': 'computedClass()',
    '[attr.data-expanded]': 'expanded() ? true : null',
  },
  template: `<ng-content />`,
})
export class Collapsible {
  // Dependencies
  private readonly uniqueId = inject(UniqueId);

  // Inputs / Outputs
  readonly expanded = model(false);
  readonly userClass = input<ClassValue>('', { alias: 'class' });

  // State
  readonly id = this.uniqueId.getId('bui-collapsible-', true);
  readonly triggerId = this.uniqueId.getId('bui-collapsible-trigger-', true);
  readonly contentId = this.uniqueId.getId('bui-collapsible-content-', true);
  protected readonly computedClass = computed(() =>
    bui(
      // Base
      'group/collapsible',
      'flex flex-col',

      // User classes
      this.userClass(),
    ),
  );

  /**
   * Toggle the collapsed state.
   * @param force Optional boolean to explicitly set the collapsed state.
   */
  toggle(force?: boolean) {
    if (force === true || force === false) {
      this.expanded.set(force);
      return;
    }

    this.expanded.update((value) => !value);
  }
}

@Directive({
  selector: '[collapsibleTrigger]',
  exportAs: 'collapsibleTriggerBehavior',
  host: {
    role: 'button',
    tabindex: '0',
    '[id]': 'triggerId',
    '[class]': 'computedClass()',
    '[attr.aria-controls]': 'contentId',
    '[attr.aria-expanded]': 'expanded()',
    '(pointerdown)': 'toggle()',
    '(keydown.space)': 'toggle()',
    '(keydown.enter)': 'toggle()',
  },
})
export class CollapsibleTriggerBehavior {
  // Dependencies
  private readonly collapsible = inject(Collapsible);

  // Inputs / Outputs
  readonly userClass = input<ClassValue>('', { alias: 'class' });

  // State
  readonly expanded = computed(() => this.collapsible.expanded());
  protected readonly triggerId = this.collapsible.triggerId;
  protected readonly contentId = this.collapsible.contentId;
  protected readonly computedClass = computed(() =>
    bui(
      // Base
      'group/collapsible-trigger',
      'relative isolate cursor-pointer rounded-md select-none',

      // User classes
      this.userClass(),
    ),
  );

  toggle() {
    this.collapsible.toggle();
  }
}

@Component({
  selector: 'collapsible-trigger',
  exportAs: 'collapsibleTrigger',
  hostDirectives: [CollapsibleTriggerBehavior],
  template: `<ng-content />`,
})
export class CollapsibleTrigger {}

@Component({
  selector: 'collapsible-content',
  exportAs: 'collapsibleContent',
  imports: [NgTemplateOutlet],
  host: {
    '[id]': 'contentId',
    '[class]': 'computedClass()',
  },
  template: `
    @if (expanded()) {
      <ng-container *ngTemplateOutlet="content" />
    }

    <ng-template #content>
      <ng-content />
    </ng-template>
  `,
})
export class CollapsibleContent {
  // Dependencies
  private readonly collapsible = inject(Collapsible);

  // Inputs / Outputs
  readonly userClass = input<ClassValue>('', { alias: 'class' });

  // State
  protected readonly expanded = computed(() => this.collapsible.expanded());
  protected readonly contentId = this.collapsible.contentId;
  protected readonly computedClass = computed(() =>
    bui(
      // Base
      'group/collapsible-content',

      // User classes
      this.userClass(),
    ),
  );
}
